import { useState, useMemo, useCallback } from "react";
import { Trans } from "@lingui/react/macro";
import { useLingui } from "@lingui/react/macro";
import { History } from "lucide-react";
import type { PaginationState } from "@tanstack/react-table";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { BaseEditableTable } from "@/components/table/BaseEditableTable";
import { EmptyState } from "@/components/table/EmptyState";
import type { EditableCellConfig } from "@/components/table/types";
import { useDocumentAccessLogs } from "@/hooks/useDocuments";
import { formatDateTime } from "@/lib/format";
import type {
  Document,
  DocumentAccessLog as AccessLog,
  DocumentAccessLogsFilters,
} from "@/types/document";

interface DocumentAccessLogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  document: Document | null;
}

const PAGE_SIZE = 20;

export function DocumentAccessLog({
  open,
  onOpenChange,
  document,
}: DocumentAccessLogProps) {
  const { t, i18n } = useLingui();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  });

  const filters = useMemo(
    (): DocumentAccessLogsFilters => ({
      page: pagination.pageIndex + 1,
      page_size: pagination.pageSize,
    }),
    [pagination]
  );

  const { data, isLoading, isFetching } = useDocumentAccessLogs(document?.id ?? "", filters);
  const logs = useMemo(() => data?.items ?? [], [data?.items]);
  const total = data?.total ?? 0;

  const handlePaginationChange = useCallback(
    (updater: PaginationState | ((old: PaginationState) => PaginationState)) => {
      setPagination((old) => (typeof updater === "function" ? updater(old) : updater));
    },
    []
  );

  const columns = useMemo((): EditableCellConfig<AccessLog>[] => {
    const formatAccessType = (type?: string) => {
      switch (type) {
        case "view":
          return t({ id: "documents.accessLogs.type.view", message: "View" });
        case "download":
          return t({ id: "documents.accessLogs.type.download", message: "Download" });
        default:
          return type ?? "—";
      }
    };

    const formatRole = (role?: string) => {
      switch (role) {
        case "admin":
          return t({ id: "documents.accessLogs.role.admin", message: "Admin" });
        case "employee":
          return t({ id: "documents.accessLogs.role.employee", message: "Employee" });
        case "parent":
          return t({ id: "documents.accessLogs.role.parent", message: "Parent" });
        default:
          return role ?? "—";
      }
    };

    return [
      {
        accessorKey: "user_display_name",
        id: "user",
        header: t({ id: "documents.accessLogs.user", message: "User" }),
        editable: false,
        sortable: false,
        cell: ({ row }) => <span>{row.original.user_display_name || row.original.user_id || "—"}</span>,
      },
      {
        accessorKey: "user_role",
        id: "role",
        header: t({ id: "documents.accessLogs.role", message: "Role" }),
        editable: false,
        sortable: false,
        cell: ({ row }) => <Badge variant="outline">{formatRole(row.original.user_role)}</Badge>,
      },
      {
        accessorKey: "document_version",
        id: "version",
        header: t({ id: "documents.accessLogs.version", message: "Version" }),
        editable: false,
        sortable: false,
        width: 90,
        cell: ({ row }) => <span>v{row.original.document_version ?? "—"}</span>,
      },
      {
        accessorKey: "access_type",
        id: "accessType",
        header: t({ id: "documents.accessLogs.accessType", message: "Type" }),
        editable: false,
        sortable: false,
        cell: ({ row }) => (
          <Badge variant="secondary">{formatAccessType(row.original.access_type)}</Badge>
        ),
      },
      {
        accessorKey: "accessed_at",
        id: "accessedAt",
        header: t({ id: "documents.accessLogs.accessedAt", message: "Accessed" }),
        editable: false,
        sortable: false,
        cell: ({ row }) => (
          <span className="tabular-nums">
            {row.original.accessed_at ? formatDateTime(row.original.accessed_at, i18n.locale) : "—"}
          </span>
        ),
      },
    ];
  }, [t, i18n.locale]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>
            <Trans id="documents.accessLogs.title">Access Logs</Trans>
            {document && (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                — {document.name}
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="sr-only">
            <Trans id="documents.accessLogs.description">Who opened or downloaded this document, and when.</Trans>
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {/* A read-only, server-paged log: no search, filters or column manager. */}
          <BaseEditableTable<AccessLog>
            data={logs}
            columns={columns}
            isLoading={isLoading}
            isFetching={isFetching}
            serverSidePagination
            totalRows={total}
            pagination={pagination}
            onPaginationChange={handlePaginationChange}
            enableSearch={false}
            enableFilters={false}
            enableColumnVisibility={false}
            enableColumnOrdering={false}
            hidePagination={total <= pagination.pageSize && pagination.pageIndex === 0}
            emptyState={
              <EmptyState
                icon={<History className="h-10 w-10" />}
                title={<Trans id="documents.accessLogs.empty">No access logs found</Trans>}
              />
            }
          />
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
